import React, { useState } from 'react';
import { Checkbox } from 'antd';
import { Field } from 'redux-form';

import { customInput } from '../../../../../common/custom-input';
import { customTextarea } from '../../../../../common/customTextarea';
import { isEditablePreStartQuestion } from '../jobShiftPreStartFormUtils';
import { formatAnswerValue, resolveParticipantName } from "../jobShiftPreStartUtils";
import styles from "../JobShiftPreStartView.module.css";

const getFlagOnValue = (questionConfig = {}) => {
  const responseConfig = questionConfig && questionConfig.response_config ? questionConfig.response_config : {};
  return responseConfig.flagOnValue !== undefined && responseConfig.flagOnValue !== null ? `${responseConfig.flagOnValue}` : "";
};

const hasCommentValue = (answer = null) => Boolean(answer && typeof answer.comment === 'string' && answer.comment.trim() !== '');

const getCommentEntryKey = (participantIndex) => `comment-${participantIndex}`;

const getRadioOptions = (questionConfig = {}) => {
  const responseConfig = questionConfig && questionConfig.response_config ? questionConfig.response_config : {};
  return Array.isArray(responseConfig.options) ? responseConfig.options : [];
};

const getRadioOptionStateClass = (optionValue, isChecked) => {
  if (!isChecked) {
    if (["yes", "pass"].includes(optionValue)) {
      return styles.PreStartOptionItemPositive;
    }
    if (["no", "fail", "not_applicable"].includes(optionValue)) {
      return styles.PreStartOptionItemNegative;
    }
    return "";
  }

  if (["yes", "pass"].includes(optionValue)) {
    return styles.PreStartOptionItemSelectedPositive;
  }

  if (["no", "fail", "not_applicable"].includes(optionValue)) {
    return styles.PreStartOptionItemSelectedNegative;
  }

  return styles.PreStartOptionItemSelectedPositive;
};

function RadioOptions({ options, selectedValue, onChange }) {
  return (
    <div className={styles.PreStartOptionList}>
      {options.map((option, optionIndex) => {
        const optionValue = option && option.value !== undefined && option.value !== null ? `${option.value}` : "";
        const isChecked = `${selectedValue || ""}` === optionValue;
        const optionStateClass = getRadioOptionStateClass(optionValue, isChecked);

        return (
          <Checkbox checked={isChecked} className={`${styles.PreStartOptionItem} ${optionStateClass}`.trim()} key={`${optionValue}-${optionIndex}`} onChange={() => onChange(optionValue)}>
            {option && option.label ? option.label : optionValue}
          </Checkbox>
        );
      })}
    </div>
  );
}

function EditableRadioField({ input, options, flagFieldName, flagLabel, flagOnValue, formChange, expandFlaggedToFullRow }) {
  const selectedValue = input && input.value ? `${input.value}` : "";
  const handleChange = (optionValue) => {
    if (formChange && optionValue !== selectedValue && flagFieldName) {
      formChange(flagFieldName, "");
    }

    if (input && input.onChange) {
      input.onChange(optionValue);
    }
  };

  const showFlaggedResponseField = Boolean(flagFieldName) && Boolean(flagOnValue) && selectedValue === flagOnValue;

  return (
    <div className={styles.PreStartEditableRadioWrap}>
      <div className={styles.PreStartEditableRadioRow}>
        <RadioOptions options={options} selectedValue={selectedValue} onChange={handleChange} />
      </div>
      {showFlaggedResponseField ? (
        <div className={`${styles.PreStartFlaggedResponseWrap} ${expandFlaggedToFullRow ? styles.PreStartFlaggedResponseWrapFullRow : ''}`.trim()}>
          <div className={styles.PreStartFlaggedResponseSeparator} />
          <div className={styles.PreStartFlaggedResponseLabel}>{flagLabel}</div>
          <Field
            name={flagFieldName}
            component={customTextarea}
            rows={2}
            className={styles.PreStartFlaggedResponseTextarea}
            otherProps={{ style: { resize: 'none' } }}
          />
        </div>
      ) : null}
    </div>
  );
}

function EditableNumberField({ fieldName, unit }) {
  return (
    <div className={styles.PreStartEditableNumberWrap}>
      <div className={styles.PreStartEditableNumberControl}>
        <Field
          name={fieldName}
          component={customInput}
          type="number"
          className={`${styles.PreStartEditableInput} ${styles.PreStartEditableNumberInput}`}
        />
      </div>
      {unit ? <span className={styles.PreStartEditableUnit}>{unit}</span> : null}
    </div>
  );
}

function EditableTextField({ fieldName }) {
  return (
    <div className={styles.PreStartEditableTextareaWrap}>
      <Field
        name={fieldName}
        component={customTextarea}
        rows={2}
        className={styles.PreStartEditableTextarea}
        otherProps={{ style: { resize: 'none' } }}
      />
    </div>
  );
}

function AnswerCell({ questionConfig, answer, fieldName, flaggedResponseFieldName, formChange, expandFlaggedToFullRow = false }) {
  const responseType = questionConfig && questionConfig.response_config ? questionConfig.response_config.type : "";
  const isEditable = Boolean(fieldName) && isEditablePreStartQuestion(questionConfig);

  if (responseType === "radio") {
    if (isEditable) {
      return (
        <Field
          name={fieldName}
          component={EditableRadioField}
          options={getRadioOptions(questionConfig)}
          flagFieldName={flaggedResponseFieldName}
          flagLabel="Flagged"
          flagOnValue={getFlagOnValue(questionConfig)}
          formChange={formChange}
          expandFlaggedToFullRow={expandFlaggedToFullRow}
        />
      );
    }

    return <RadioOptions options={getRadioOptions(questionConfig)} selectedValue={answer && answer.value} onChange={() => {}} />;
  }

  if (responseType === "number" && isEditable) {
    const unit = questionConfig && questionConfig.response_config && typeof questionConfig.response_config.unit === 'string'
      ? questionConfig.response_config.unit.trim()
      : '';

    return <EditableNumberField fieldName={fieldName} unit={unit} />;
  }

  if (responseType === "text" && isEditable) {
    return <EditableTextField fieldName={fieldName} />;
  }

  if (responseType === "signature") {
    const signatureValue = formatAnswerValue(questionConfig, answer);

    return signatureValue ? (
      <img className={styles.PreStartSignatureImage} src={signatureValue} alt="Signature" />
    ) : (
      <span className={styles.PreviewValue}>-</span>
    );
  }

  if (responseType === "photo") {
    const photoValue = formatAnswerValue(questionConfig, answer);

    return (
      <div className={styles.PreStartAttachmentBox}>
        <label className={styles.PreStartAttachmentLabel}>
          <Checkbox checked={Boolean(photoValue)} disabled />
          <span>Photo Attached</span>
          <svg className={styles.PreStartAttachmentIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">
            <path fill="currentColor" d="M384 96C384 78.3 398.3 64 416 64L544 64C561.7 64 576 78.3 576 96L576 224C576 241.7 561.7 256 544 256C526.3 256 512 241.7 512 224L512 173.3L374.6 310.6C362.1 323.1 341.8 323.1 329.3 310.6C316.8 298.1 316.8 277.8 329.3 265.3L466.7 128L416 128C398.3 128 384 113.7 384 96zM96 160C96 124.7 124.7 96 160 96L288 96C305.7 96 320 110.3 320 128C320 145.7 305.7 160 288 160L160 160L160 480L480 480L480 352C480 334.3 494.3 320 512 320C529.7 320 544 334.3 544 352L544 480C544 515.3 515.3 544 480 544L160 544C124.7 544 96 515.3 96 480L96 160z" />
          </svg>
        </label>
      </div>
    );
  }

  const answerText = formatAnswerValue(questionConfig, answer);
  return <span className={styles.PreviewValue}>{answerText || "-"}</span>;
}

function CommentIconButton({ active, onClick }) {
  return (
    <button
      type="button"
      className={`${styles.PreStartCommentButton} ${active ? styles.PreStartCommentButtonVisible : ''}`.trim()}
      onClick={onClick}
      title={active ? 'Comment added' : 'Add comment'}
      aria-label={active ? 'Comment added' : 'Add comment'}
    >
      <svg className={styles.PreStartCommentIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" aria-hidden="true">
        <path d="M576 304C576 436.5 461.4 544 320 544C282.9 544 247.7 536.6 215.9 523.3L97.5 574.1C88.1 578.1 77.3 575.8 70.4 568.3C63.5 560.8 62 549.8 66.8 540.8L115.6 448.6C83.2 408.3 64 358.3 64 304C64 171.5 178.6 64 320 64C461.4 64 576 171.5 576 304z" />
      </svg>
    </button>
  );
}

export default function PreStartQuestionView({ question, number, supervisorParticipant, supervisorAnswer, supervisorFieldName, supervisorCommentFieldName, supervisorFlaggedResponseFieldName, workerAnswers = [], formChange }) {
  const questionConfig = question || {};
  const answerScope = questionConfig && questionConfig.answer_scope ? questionConfig.answer_scope : "supervisor";
  const isWorkerScope = answerScope === "worker";
  const questionText = questionConfig && questionConfig.question_text ? questionConfig.question_text : "";
  const guidanceText = questionConfig && questionConfig.guidance_text ? questionConfig.guidance_text : "";
  const [openCommentKeys, setOpenCommentKeys] = useState({});
  const workerScopeAnswers = [
    ...(supervisorParticipant ? [{ participant: supervisorParticipant, participantIndex: "supervisor", answer: supervisorAnswer, fieldName: supervisorFieldName, commentFieldName: supervisorCommentFieldName, flaggedResponseFieldName: supervisorFlaggedResponseFieldName }] : []),
    ...workerAnswers
  ];
  const commentEntries = (isWorkerScope ? workerScopeAnswers : [{
    participant: supervisorParticipant,
    participantIndex: 'supervisor',
    answer: supervisorAnswer,
    commentFieldName: supervisorCommentFieldName
  }]).map((entry) => {
    const entryKey = getCommentEntryKey(entry && entry.participantIndex);
    const participantName = resolveParticipantName(entry && entry.participant);
    const hasComment = hasCommentValue(entry && entry.answer);
    const isVisible = Boolean(openCommentKeys[entryKey]) || hasComment;

    return {
      ...entry,
      entryKey,
      participantName,
      hasComment,
      isVisible
    };
  });
  const visibleCommentEntries = commentEntries.filter(({ isVisible, commentFieldName }) => Boolean(isVisible && commentFieldName));

  const showComment = (entryKey) => {
    setOpenCommentKeys((prevState) => ({
      ...prevState,
      [entryKey]: true
    }));
  };

  const hideComment = (entryKey, commentFieldName) => {
    if (formChange && commentFieldName) {
      formChange(commentFieldName, "");
    }

    setOpenCommentKeys((prevState) => {
      const nextState = { ...prevState };
      delete nextState[entryKey];
      return nextState;
    });
  };

  return (
    <tr>
      <td className={styles.PreStartQuestionNumber}>{number}</td>
      <td className={styles.PreStartQuestionTextCell} colSpan={2}>
        <div className={styles.PreStartQuestionTextLine}>
          <span>{questionText}</span>
          {isWorkerScope ? <span className={styles.PreStartQuestionScope}>Per-Worker</span> : null}
        </div>
        {guidanceText ? <div className={styles.PreStartQuestionGuidance}>{guidanceText}</div> : null}
        {visibleCommentEntries.length > 0 ? (
          <div className={styles.PreStartCommentStack}>
            {visibleCommentEntries.map(({ entryKey, participantName, commentFieldName }) => (
              <div className={styles.PreStartCommentCard} key={entryKey}>
                <div className={styles.PreStartCommentHeader}>
                  <div className={styles.PreStartCommentLabel}>{isWorkerScope ? `Comment${participantName ? ` (${participantName})` : ''}` : 'Comment'}</div>
                  <button type="button" className={styles.PreStartCommentRemoveButton} onClick={() => hideComment(entryKey, commentFieldName)}>
                    Remove comment
                  </button>
                </div>
                <Field
                  name={commentFieldName}
                  component={customTextarea}
                  rows={2}
                  className={styles.PreStartCommentTextarea}
                  otherProps={{ style: { resize: 'none' } }}
                />
              </div>
            ))}
          </div>
        ) : null}
      </td>
      <td className={styles.PreStartQuestionAnswerCell}>
        {isWorkerScope ? (
          <div className={styles.PreStartAnswerStack}>
            {workerScopeAnswers.length > 0 ? workerScopeAnswers.map(({ participant, participantIndex, answer, fieldName, commentFieldName, flaggedResponseFieldName }) => {
              const entryKey = getCommentEntryKey(participantIndex);
              const commentVisible = Boolean(openCommentKeys[entryKey]) || hasCommentValue(answer);

              return (
                <div className={styles.PreStartAnswerRow} key={`${participant && (participant.staff_id || participant.added_person_name || "worker")}-${participantIndex}`}>
                  <span className={styles.PreStartAnswerName}>{resolveParticipantName(participant) || "Unnamed worker"}</span>
                  <div className={styles.PreStartAnswerContent}>
                    <div className={styles.PreStartAnswerBox}>
                      <CommentIconButton active={commentVisible} onClick={() => showComment(entryKey)} />
                      <AnswerCell questionConfig={questionConfig} answer={answer} fieldName={fieldName} flaggedResponseFieldName={flaggedResponseFieldName} formChange={formChange} expandFlaggedToFullRow />
                    </div>
                  </div>
                </div>
              );
            }) : (
              <span className={styles.PreStartAnswerEmpty}>No worker responses</span>
            )}
          </div>
        ) : (
          <div className={styles.PreStartAnswerRow}>
            <div className={styles.PreStartAnswerContent}>
              <div className={styles.PreStartAnswerBox}>
                <CommentIconButton active={Boolean(openCommentKeys[getCommentEntryKey('supervisor')]) || hasCommentValue(supervisorAnswer)} onClick={() => showComment(getCommentEntryKey('supervisor'))} />
                <AnswerCell questionConfig={questionConfig} answer={supervisorAnswer} fieldName={supervisorFieldName} flaggedResponseFieldName={supervisorFlaggedResponseFieldName} formChange={formChange} />
              </div>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}